import { useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'sonner';

import { CalculatorCalendar } from './calculator-calendar';
import { CalculatorLegend } from './calculator-legend';
import { combineDateAndTime, formatCivilDate, instantToCivilUtc } from './civil-date';
import { CivilDatePicker } from './civil-date-picker';
import { CountingReference } from './counting-reference';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import {
  resolveProceduralDate,
  type ResolveProceduralDateResult,
} from '../../../../../backend/src/matter/procedural-timeline/date-resolution';
import { useUpdateProceduralEvent } from '@/api/mutations/useUpdateProceduralEvent';
import { useProceduralHolidays } from '@/api/queries/useProceduralHolidays';
import { usePermission } from '@/hooks/useHasPermission';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { COUNTING_OPTIONS, CountingKey, useTimelineCalculatorStore } from '@/new/stores/timeline-calculator-store';

const POPOVER_WIDTH = 480;
// Distance from the bottom (px) within which the scroll area counts as "read".
const SCROLL_BOTTOM_THRESHOLD = 8;

export function TimelineCalculator() {
  const prefill = useTimelineCalculatorStore((s) => s.prefill);
  const position = useTimelineCalculatorStore((s) => s.position);
  const close = useTimelineCalculatorStore((s) => s.close);
  const setPosition = useTimelineCalculatorStore((s) => s.setPosition);

  if (prefill === null) return null;

  return (
    <CalculatorBody
      key={`${prefill.eventId ?? 'new'}-${prefill.matterId}`}
      position={position}
      onPositionChange={setPosition}
      onClose={close}
      prefill={prefill}
    />
  );
}

interface BodyProps {
  position: { x: number; y: number };
  onPositionChange: (x: number, y: number) => void;
  onClose: () => void;
  prefill: NonNullable<ReturnType<typeof useTimelineCalculatorStore.getState>['prefill']>;
}

function CalculatorBody({ position, onPositionChange, onClose, prefill }: BodyProps) {
  const [startDate, setStartDate] = useState<Date | null>(instantToCivilUtc(prefill.startDate));
  const [days, setDays] = useState<number>(prefill.days);
  const [counting, setCounting] = useState<CountingKey>(prefill.counting);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>(prefill.startTime);
  const [endDate, setEndDate] = useState<Date | null>(instantToCivilUtc(prefill.endDate));
  const [endTime, setEndTime] = useState<string>(prefill.endTime);

  const option = useMemo(() => COUNTING_OPTIONS.find((o) => o.key === counting) ?? COUNTING_OPTIONS[0], [counting]);

  const yearRange = useMemo(() => {
    const anchorYear = startDate?.getUTCFullYear() ?? new Date().getUTCFullYear();
    return { fromYear: anchorYear - 1, toYear: anchorYear + 2 };
  }, [startDate]);

  const { data: holidays } = useProceduralHolidays({
    matterId: prefill.matterId,
    jurisdiction: option.jurisdiction,
    fromYear: yearRange.fromYear,
    toYear: yearRange.toYear,
  });

  const updateEvent = useUpdateProceduralEvent();
  const { allowed: canEdit } = usePermission(Permission.ProceduralTimelineEdit);

  const [recalculateDependents, setRecalculateDependents] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const dependentCount = prefill.dependentCount;
  const showDependentToggle = prefill.eventId !== null && dependentCount > 0;

  // Recompute on every input change; there is no explicit calculate step.
  const computation = useMemo<{ result: ResolveProceduralDateResult | null; error: string | null }>(() => {
    if (startDate === null || !Number.isFinite(days) || days === 0 || holidays === undefined) {
      return { result: null, error: null };
    }
    try {
      const r = resolveProceduralDate({
        startDate,
        interval: { days },
        jurisdiction: option.jurisdiction,
        dayCount: option.dayCount,
        holidays,
      });
      return { result: r, error: null };
    } catch (err) {
      return { result: null, error: err instanceof Error ? err.message : 'Calculation failed.' };
    }
  }, [startDate, days, option, holidays]);
  const result = computation.result;

  const endBeforeStart = selectedDate !== null && endDate !== null && endDate.getTime() < selectedDate.getTime();
  const errorMessage = computation.error ?? (endBeforeStart ? 'End date must be on or after the result date.' : null);

  // Gate the update until the user has scrolled through the calculation.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (el === null) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD) setScrolledToEnd(true);
  };

  // A fresh result (from changed inputs) moves the selected date and re-gates the
  // scroll-to-review. The time of day is left untouched: it's the event's own time,
  // shown in an editable field, and independent of which day the date lands on.
  useEffect(() => {
    if (result === null) {
      setSelectedDate(null);
      setScrolledToEnd(false);
      return;
    }
    setSelectedDate(result.resultDate);
    const el = scrollRef.current;
    if (el === null) return;
    setScrolledToEnd(el.scrollHeight - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD);
  }, [result]);

  const runUpdate = async () => {
    if (prefill.eventId === null || selectedDate === null) return;
    const iso = combineDateAndTime(selectedDate, selectedTime);
    const endIso = endDate !== null ? combineDateAndTime(endDate, endTime) : undefined;
    try {
      await updateEvent.mutateAsync({
        matterId: prefill.matterId,
        eventId: prefill.eventId,
        input: {
          startDateTime: iso,
          ...(endIso !== undefined ? { endDateTime: endIso } : {}),
          recalculateDependents,
        },
      });
      toast.success('Event date updated.');
      setIsConfirmOpen(false);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update event.');
    }
  };

  const handleSubmit = () => {
    if (prefill.eventId === null) {
      onClose();
      return;
    }
    if (selectedDate === null || endBeforeStart) return;
    // Moving the start date cascades to dependents, so confirm before saving.
    if (dependentCount > 0) {
      setIsConfirmOpen(true);
      return;
    }
    void runUpdate();
  };

  const updateButton = (
    <Button
      variant="primary"
      type="button"
      onClick={handleSubmit}
      disabled={
        !canEdit ||
        updateEvent.isLoading ||
        result === null ||
        prefill.eventId === null ||
        selectedDate === null ||
        endBeforeStart ||
        !scrolledToEnd
      }
    >
      {updateEvent.isLoading ? <Icon className="lucide--loader-2 animate-spin" /> : <Icon className="lucide--check" />}
      Update event
    </Button>
  );

  return (
    <>
      <div
        className="fixed z-50 flex w-[480px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
        style={{
          left: position.x,
          top: position.y,
          width: POPOVER_WIDTH,
          // Prevent clipping
          maxHeight: `min(70vh, calc(100vh - ${position.y}px - 24px))`,
        }}
      >
        <DragHandle
          position={position}
          onPositionChange={onPositionChange}
          onClose={onClose}
          title="Timeline calculator"
          subtitle={prefill.eventName ?? 'New calculation'}
        />

        <div ref={scrollRef} onScroll={handleScroll} className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          <Inputs
            startDate={startDate}
            onStartDateChange={setStartDate}
            days={days}
            onDaysChange={setDays}
            counting={counting}
            onCountingChange={setCounting}
          />

          {errorMessage !== null && (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {errorMessage}
            </p>
          )}

          {result !== null && startDate !== null && (
            <ResultBlock
              result={result}
              startDate={startDate}
              selectedDate={selectedDate ?? result.resultDate}
              onSelectDate={(d) => setSelectedDate(d)}
              selectedTime={selectedTime}
              onSelectedTimeChange={setSelectedTime}
              endDate={endDate}
              onEndDateChange={setEndDate}
              endTime={endTime}
              onEndTimeChange={setEndTime}
              canEdit={canEdit}
            />
          )}

          {result !== null && showDependentToggle && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
              <p>
                Other procedural events are dependent on this event. Ensure subsequent events are reviewed upon update.
              </p>
            </div>
          )}

          {result !== null && showDependentToggle && (
            <div className="flex items-center gap-3">
              <Switch
                id="calc-recalc-dependents"
                checked={recalculateDependents}
                onCheckedChange={setRecalculateDependents}
                disabled={!canEdit}
              />
              <div className="space-y-1">
                <label
                  htmlFor="calc-recalc-dependents"
                  className="block cursor-pointer text-sm font-medium text-gray-800"
                >
                  Suggest updates to dependents
                </label>
                <p className="text-xs text-gray-500">
                  Recalculate the dates of events that follow on from this one and surface them as suggestions for
                  review. No events will be directly updated other than the current event.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 p-3">
          <span className="text-xs text-gray-400">
            {result !== null && !scrolledToEnd ? 'Scroll to review the full calculation.' : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            {updateButton}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Update this event?"
        onConfirm={() => void runUpdate()}
        isLoading={updateEvent.isLoading}
      >
        <p className="text-sm text-gray-700">
          {dependentCount === 1
            ? 'Another event is calculated from this one.'
            : `${dependentCount} events are calculated from this one.`}
        </p>
        <p className="mt-3 text-sm text-gray-600">
          {recalculateDependents
            ? 'Their dates will be recalculated and proposed as suggestions for you to review. No dependent events are changed directly.'
            : "Their dates won't be recalculated, so they may no longer line up with this change."}
        </p>
      </ConfirmationModal>
    </>
  );
}

interface InputsProps {
  startDate: Date | null;
  onStartDateChange: (d: Date | null) => void;
  days: number;
  onDaysChange: (d: number) => void;
  counting: CountingKey;
  onCountingChange: (k: CountingKey) => void;
}

function Inputs({ startDate, onStartDateChange, days, onDaysChange, counting, onCountingChange }: InputsProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">Source date</Label>
          <CivilDatePicker value={startDate} onChange={onStartDateChange} />
        </div>
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">Days elapsed</Label>
          <DaysInput days={days} onDaysChange={onDaysChange} />
        </div>
      </div>
      <div>
        <div className="mb-1 flex items-center gap-1.5">
          <Label className="mb-0 text-xs font-medium text-gray-700">Counting method</Label>
          <CountingReference />
        </div>
        <Select value={counting} onValueChange={(v) => onCountingChange(v as CountingKey)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COUNTING_OPTIONS.map((opt) => (
              <SelectItem key={opt.key} value={opt.key}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function DaysInput({ days, onDaysChange }: { days: number; onDaysChange: (n: number) => void }) {
  const [text, setText] = useState<string>(days === 0 ? '' : String(days));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    const parsed = parseInt(v, 10);
    if (Number.isFinite(parsed)) {
      setText(String(parsed));
      onDaysChange(parsed);
    } else {
      setText(v);
      onDaysChange(0);
    }
  };

  const handleBlur = () => {
    if (text === '' || text === '-') {
      setText('0');
      onDaysChange(0);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (text === '0') setText('');
    const target = e.currentTarget;
    setTimeout(() => target.select(), 0);
  };

  return (
    <Input
      type="number"
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className="w-full"
    />
  );
}

interface ResultBlockProps {
  result: ResolveProceduralDateResult;
  startDate: Date;
  selectedDate: Date;
  onSelectDate: (d: Date) => void;
  selectedTime: string;
  onSelectedTimeChange: (t: string) => void;
  endDate: Date | null;
  onEndDateChange: (d: Date | null) => void;
  endTime: string;
  onEndTimeChange: (t: string) => void;
  canEdit: boolean;
}

function ResultBlock({
  result,
  startDate,
  selectedDate,
  onSelectDate,
  selectedTime,
  onSelectedTimeChange,
  endDate,
  onEndDateChange,
  endTime,
  onEndTimeChange,
  canEdit,
}: ResultBlockProps) {
  return (
    <div className="flex flex-col gap-3">
      <CalculatorLegend result={result} />
      <CalculatorCalendar
        result={result}
        startDate={startDate}
        selectedDate={selectedDate}
        onSelectDate={onSelectDate}
      />

      <div className="flex flex-col gap-1 text-xs text-gray-700">
        {result.startDateExcluded && (
          <p>
            <span className="text-gray-400">↳ </span>
            <strong>Day 0, {formatCivilDate(startDate, 'EEEE d MMMM yyyy')}</strong>: start day, not counted.
          </p>
        )}
        <p>
          <span className="text-gray-400">↳ </span>
          Counted {result.countedDates.length} {labelForCounting(result, result.countedDates.length)} between the start
          and the result.
        </p>
        {result.endDateRolledForward && (
          <p>
            <span className="text-gray-400">↳ </span>
            {result.direction === 'before'
              ? 'Result moved back to the previous business day.'
              : 'Result rolled forward to the next business day.'}
          </p>
        )}
        <p className="text-teal-700">
          <span className="text-gray-400">↳ </span>
          <strong>Result: {formatCivilDate(result.resultDate, 'EEEE d MMMM yyyy')}.</strong>
        </p>
        {result.withinDate !== null && result.withinDate !== undefined && (
          <p className="text-amber-700">
            <span className="text-amber-500">↳ </span>
            <strong>Within date: {formatCivilDate(result.withinDate, 'EEEE d MMMM yyyy')}.</strong> Use this if the
            deadline is expressed as "within X days", which excludes only the start day rather than both ends (CPR 2.8).
          </p>
        )}
        {result.warnings.map((w) => (
          <p key={w} className="text-amber-700">
            <span className="text-amber-500">↳ </span>
            {w}
          </p>
        ))}
      </div>

      {!canEdit && (
        <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
          View only. You don't have permission to update this event.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">Event start date</Label>
          <CivilDatePicker value={selectedDate} onChange={(d) => d !== null && onSelectDate(d)} disabled={!canEdit} />
        </div>
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">Start time (optional)</Label>
          <Input
            type="time"
            value={selectedTime}
            onChange={(e) => onSelectedTimeChange(e.target.value)}
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">End date (optional)</Label>
          <CivilDatePicker
            value={endDate}
            onChange={onEndDateChange}
            clearable
            placeholder="Pick a date"
            disabled={!canEdit}
          />
        </div>
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">End time (optional)</Label>
          <Input
            type="time"
            value={endTime}
            onChange={(e) => onEndTimeChange(e.target.value)}
            disabled={!canEdit || endDate === null}
          />
        </div>
      </div>
    </div>
  );
}

interface DragHandleProps {
  position: { x: number; y: number };
  onPositionChange: (x: number, y: number) => void;
  onClose: () => void;
  title: string;
  subtitle: string;
}

function DragHandle({ position, onPositionChange, onClose, title, subtitle }: DragHandleProps) {
  const dragState = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      pointerId: e.pointerId,
      offsetX: e.clientX - position.x,
      offsetY: e.clientY - position.y,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragState.current === null || dragState.current.pointerId !== e.pointerId) return;
    const nextX = Math.max(0, Math.min(window.innerWidth - POPOVER_WIDTH, e.clientX - dragState.current.offsetX));
    const nextY = Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragState.current.offsetY));
    onPositionChange(nextX, nextY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragState.current?.pointerId === e.pointerId) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      dragState.current = null;
    }
  };

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className="flex shrink-0 cursor-grab items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2 select-none active:cursor-grabbing"
    >
      <div className="flex items-center gap-2 overflow-hidden">
        <Icon className="lucide--grip-vertical text-gray-400" />
        <div className="overflow-hidden">
          <p className="truncate text-sm font-medium text-gray-900">{title}</p>
          <p className="truncate text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="About the timeline calculator"
            >
              <Icon className="lucide--info" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="max-w-xs text-left leading-relaxed">
            Calculate a procedural deadline from a source date. Pick a source date, the number of days elapsed, and a
            counting method. The calculator resolves the deadline using bank-holiday data for the chosen jurisdiction
            and shows which days counted and which were skipped. Verify and adjust the result before applying it to the
            event.
          </TooltipContent>
        </Tooltip>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onClose}
          aria-label="Close calculator"
        >
          <Icon className="lucide--x" />
        </Button>
      </div>
    </div>
  );
}

function labelForCounting(result: ResolveProceduralDateResult, count: number): string {
  const noun = Math.abs(count) === 1 ? 'day' : 'days';
  switch (result.dayCount) {
    case 'calendar':
      return `calendar ${noun}`;
    case 'business':
      return `business ${noun}`;
    case 'clear-cpr':
      return `clear ${noun}`;
  }
}
